/**
 * @fileoverview Toast provider that wires the toast context to the container.
 *
 * @purpose Supply `toast` helper and render `ToastContainer` inside the provider so notifications are GLOBAL.
 * @usage Mounted in `layout.tsx` to keep toast queue across the app.
 * @ownership Shared UI components (core)
 * @dependencies
 * - react (createContext/useContext)
 * - @korvo/core/hooks/useToast
 * - ./ToastContainer
 * @restrictions
 * - DO NOT use the context outside of a provider.
 */
'use client'
import { useToast } from '@korvo/core/client/hooks/useToast';
import { ERR_INTERNAL_500 } from '@korvo/core/lib/error-codes';
import { KorvoError } from '@korvo/core/lib/korvo-error';
import { createContext, type ReactNode, useContext } from 'react'

import { type ToastContextType } from './types'

export const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function ToastProvider({ children }: { children: ReactNode }) {
  const { toast, toasts, removeToast } = useToast()

  return (
    <ToastContext.Provider value={{ toast, toasts, removeToast }}>
      {children}
    </ToastContext.Provider>
  )
}

export function useToastContext() {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new KorvoError(ERR_INTERNAL_500, 1, { message: 'useToastContext must be used within a ToastProvider' })
  }
  return context
}
